export const PHASES = ['solid', 'liquid', 'gas', 'plasma'] as const;
export type Phase = typeof PHASES[number];
export type RGB = readonly [number, number, number];

export interface MaterialDefinition {
  /** Approximate specific sensible heat, J/(kg K), by phase. */
  readonly specificHeat: Readonly<Record<Phase, number>>;
  readonly molarMassKg: number;
  readonly solidDensityKgM3: number;
  readonly liquidDensityKgM3: number;
  readonly name: string;
  readonly elements: Readonly<Record<string, number>>;
  readonly meltingPoint: number;
  readonly boilingPoint: number;
  readonly triplePressure: number;
  readonly ionizationTemperature: number;
  readonly vaporTemperatureScale: number;
  readonly fusionHeat: number;
  readonly vaporHeat: number;
  readonly ionizationHeat: number;
  readonly greenhouse: number;
  readonly cloudOpacity: number;
  readonly frost: boolean;
  readonly colour: Readonly<Record<Phase, RGB>>;
  readonly albedo: Readonly<Record<Phase, number>>;
  readonly cloudAlbedo: number;
  readonly cloudInfrared: number;
}

// Integer atomic weights and constant condensed densities are explicit model
// approximations, not precision equations of state.
const ATOMIC_MASS = { H: 1, He: 4, C: 12, N: 14, O: 16, Mg: 24, Si: 28, Ca: 40, Fe: 56 } as const;
const DENSITIES: Record<string, readonly [number, number]> = {
  Water: [917, 1000], Methane: [520, 450], Ammonia: [820, 680], Nitrogen: [1020, 810],
  Oxygen: [1420, 1140], 'Carbon dioxide': [1560, 1100], Hydrogen: [88, 70], Helium: [190, 125],
  Iron: [7870, 7000], 'Organic carbon': [2000, 1600],
};

function material(name: string, elements: Record<string, number>, meltingPoint: number,
  boilingPoint: number, solid: RGB, liquid: RGB, gas: RGB,
  options: Partial<Omit<MaterialDefinition, 'name' | 'elements' | 'meltingPoint' | 'boilingPoint' | 'colour'>> = {},
): MaterialDefinition {
  for (const [element, amount] of Object.entries(elements)) {
    if (!(element in ATOMIC_MASS) || !Number.isFinite(amount) || amount <= 0) {
      throw new RangeError(`Material ${name} requires a registered atomic mass and positive formula coefficient for ${element}.`);
    }
  }
  return {
    specificHeat: { solid: 800, liquid: 2000, gas: 1000, plasma: 1200 },
    molarMassKg: Object.entries(elements).reduce((sum, [element, amount]) => sum + ATOMIC_MASS[element as keyof typeof ATOMIC_MASS] * amount, 0) / 1000,
    solidDensityKgM3: DENSITIES[name]?.[0] ?? 3200,
    liquidDensityKgM3: DENSITIES[name]?.[1] ?? 2800,
    name, elements, meltingPoint, boilingPoint,
    triplePressure: 0.001, ionizationTemperature: 12_000,
    vaporTemperatureScale: boilingPoint * 13, fusionHeat: 3, vaporHeat: 18, ionizationHeat: 100,
    greenhouse: 0, cloudOpacity: 9, frost: meltingPoint < 400,
    albedo: { solid: meltingPoint < 400 ? 0.65 : 0.3, liquid: 0.08, gas: 0.15, plasma: 0 },
    cloudAlbedo: 0.64, cloudInfrared: 5,
    ...options,
    colour: { solid, liquid, gas, plasma: [1, 0.75, 0.45] },
  };
}

/** Reference phase points, with deliberately approximate artwork optics/rates.
 * Water/methane/ammonia reference data: NIST Chemistry WebBook (docs/climate.md).
 * Mineral entries are representative compounds, not a mineral phase database.
 */
export const MATERIALS = {
  water: material('Water', { H: 2, O: 1 }, 273.15, 373.15, [0.72, 0.8, 0.85], [0.008, 0.15, 0.31], [0.4, 0.64, 0.88],
    { triplePressure: 0.00612, greenhouse: 14, vaporTemperatureScale: 5000,
      specificHeat: { solid: 2100, liquid: 4180, gas: 1900, plasma: 2000 } }),
  methane: material('Methane', { C: 1, H: 4 }, 90.67, 111.66, [0.76, 0.81, 0.78], [0.19, 0.14, 0.055], [0.12, 0.52, 0.85],
    { triplePressure: 0.117, greenhouse: 18, vaporTemperatureScale: 1000, cloudAlbedo: 0.5 }),
  ammonia: material('Ammonia', { N: 1, H: 3 }, 195.4, 239.82, [0.84, 0.8, 0.64], [0.26, 0.33, 0.27], [0.72, 0.66, 0.42],
    { triplePressure: 0.0606, greenhouse: 20, vaporTemperatureScale: 2800, cloudAlbedo: 0.72, cloudInfrared: 8 }),
  nitrogen: material('Nitrogen', { N: 2 }, 63.15, 77.36, [0.78, 0.83, 0.88], [0.18, 0.29, 0.38], [0.16, 0.52, 0.95]),
  oxygen: material('Oxygen', { O: 2 }, 54.36, 90.19, [0.6, 0.78, 0.93], [0.14, 0.4, 0.62], [0.14, 0.53, 0.96]),
  carbonDioxide: material('Carbon dioxide', { C: 1, O: 2 }, 216.58, 194.67, [0.85, 0.84, 0.79], [0.23, 0.27, 0.28], [0.81, 0.39, 0.3],
    { triplePressure: 5.18, greenhouse: 30 }),
  hydrogen: material('Hydrogen', { H: 1 }, 13.99, 20.27, [0.84, 0.87, 0.89], [0.3, 0.41, 0.5], [0.49, 0.61, 0.78], { ionizationTemperature: 9000 }),
  helium: material('Helium', { He: 1 }, 0.95, 4.22, [0.85, 0.87, 0.91], [0.35, 0.45, 0.53], [0.7, 0.64, 0.49], { triplePressure: 25, ionizationTemperature: 20_000 }),
  silicate: material('Calcium silicate', { Ca: 1, Si: 1, O: 3 }, 1813, 3200, [0.48, 0.43, 0.35], [0.85, 0.25, 0.06], [0.6, 0.47, 0.31]),
  silica: material('Silica', { Si: 1, O: 2 }, 1986, 3223, [0.68, 0.64, 0.56], [0.92, 0.43, 0.14], [0.7, 0.6, 0.44]),
  basalt: material('Magnesium silicate', { Mg: 1, Si: 1, O: 3 }, 1557, 3100, [0.13, 0.14, 0.16], [0.86, 0.2, 0.025], [0.5, 0.42, 0.31],
    { albedo: { solid: 0.11, liquid: 0.08, gas: 0.15, plasma: 0 } }),
  iron: material('Iron', { Fe: 1 }, 1811, 3134, [0.3, 0.26, 0.22], [0.88, 0.42, 0.09], [0.72, 0.51, 0.33]),
  ironOxide: material('Iron oxide', { Fe: 2, O: 3 }, 1838, 3400, [0.75, 0.19, 0.06], [0.93, 0.28, 0.025], [0.73, 0.24, 0.13]),
  carbonate: material('Calcium carbonate', { Ca: 1, C: 1, O: 3 }, 1612, 3300, [0.72, 0.67, 0.55], [0.92, 0.59, 0.23], [0.72, 0.63, 0.47]),
  carbon: material('Organic carbon', { C: 1 }, 3800, 4300, [0.035, 0.07, 0.025], [0.11, 0.09, 0.06], [0.14, 0.13, 0.1],
    { albedo: { solid: 0.07, liquid: 0.04, gas: 0.1, plasma: 0 } }),
} satisfies Record<string, MaterialDefinition>;
export type MaterialId = keyof typeof MATERIALS;

export interface MaterialInventory {
  readonly material: MaterialId;
  readonly phases: Record<Phase, number>;
  /** Subsets of solid/liquid amounts, not additional matter. */
  readonly suspended: { solid: number; liquid: number };
}

export function inventory(material: MaterialId, amounts: Partial<Record<Phase, number>>): MaterialInventory {
  const phases = { solid: 0, liquid: 0, gas: 0, plasma: 0, ...amounts };
  if (Object.values(phases).some(value => !Number.isFinite(value) || value < 0)) throw new RangeError('Material amounts must be finite and non-negative.');
  return { material, phases, suspended: { solid: 0, liquid: 0 } };
}

export function totalMaterial(stock: MaterialInventory): number {
  return PHASES.reduce((sum, phase) => sum + stock.phases[phase], 0);
}

/** Conserved transfer with suspended condensates tracked through phase changes. */
export function movePhase(stock: MaterialInventory, from: Phase, to: Phase, requested: number, condensing = false): number {
  const amount = Math.min(stock.phases[from], Math.max(0, requested));
  if (amount === 0 || from === to) return 0;
  let airborne = 0;
  if (from === 'solid' || from === 'liquid') {
    airborne = amount * stock.suspended[from] / Math.max(1e-12, stock.phases[from]);
    stock.suspended[from] = Math.max(0, stock.suspended[from] - airborne);
  }
  stock.phases[from] -= amount;
  stock.phases[to] += amount;
  if (to === 'solid' || to === 'liquid') stock.suspended[to] += condensing ? amount : airborne;
  return amount;
}

export function saturationPressure(definition: MaterialDefinition, temperature: number): number {
  return Math.exp(Math.max(-60, Math.min(30,
    definition.vaporTemperatureScale * (1 / definition.boilingPoint - 1 / Math.max(1, temperature)),
  )));
}

/** Returns latent heat consumed (negative for heat released). All materials
 * follow this same kinetic phase/condensation/precipitation path.
 */
export function evolvePhases(stock: MaterialInventory, temperature: number, pressure: number, dt: number, pressurePerInventoryUnit = 1): number {
  if (!Number.isFinite(dt) || dt <= 0) return 0;
  if (!Number.isFinite(pressurePerInventoryUnit) || pressurePerInventoryUnit <= 0) throw new RangeError('Phase pressure conversion must be finite and positive.');
  const definition = MATERIALS[stock.material];
  const blend = 1 - Math.exp(-dt / 20);
  const total = totalMaterial(stock);
  let latentHeat = 0;
  const plasmaFraction = Math.max(0, Math.min(1,
    (temperature / (definition.ionizationTemperature * (1 + 0.03 * Math.log1p(pressure))) - 0.7) / 0.6,
  ));
  const plasmaGoal = total * plasmaFraction;
  if (plasmaGoal > stock.phases.plasma) {
    const need = (plasmaGoal - stock.phases.plasma) * blend;
    const available = Math.max(1e-12, total - stock.phases.plasma);
    for (const phase of ['solid', 'liquid', 'gas'] as const) {
      latentHeat += movePhase(stock, phase, 'plasma', need * stock.phases[phase] / available) *
        (definition.ionizationHeat + (phase !== 'gas' ? definition.vaporHeat : 0) + (phase === 'solid' ? definition.fusionHeat : 0));
    }
  } else latentHeat -= movePhase(stock, 'plasma', 'gas', (stock.phases.plasma - plasmaGoal) * blend) * definition.ionizationHeat;

  const condensedPhase = temperature >= definition.meltingPoint && pressure >= definition.triplePressure ? 'liquid' : 'solid';
  const otherPhase = condensedPhase === 'liquid' ? 'solid' : 'liquid';
  const melted = movePhase(stock, otherPhase, condensedPhase, stock.phases[otherPhase] * blend);
  latentHeat += melted * definition.fusionHeat * (condensedPhase === 'liquid' ? 1 : -1);
  const gasGoal = pressure < definition.triplePressure && temperature >= definition.meltingPoint
    ? total - stock.phases.plasma
    : Math.min(total - stock.phases.plasma, saturationPressure(definition, temperature) / pressurePerInventoryUnit);
  if (stock.phases.gas > gasGoal) {
    const condensed = movePhase(stock, 'gas', condensedPhase, (stock.phases.gas - gasGoal) * blend, true);
    latentHeat -= condensed * (definition.vaporHeat + (condensedPhase === 'solid' ? definition.fusionHeat : 0));
  } else {
    let deficit = (gasGoal - stock.phases.gas) * blend;
    for (const phase of ['liquid', 'solid'] as const) {
      const evaporated = movePhase(stock, phase, 'gas', deficit);
      deficit -= evaporated;
      latentHeat += evaporated * (definition.vaporHeat + (phase === 'solid' ? definition.fusionHeat : 0));
    }
  }
  const settledFraction = 1 - Math.exp(-dt / (8 + 35 * Math.min(1, pressure)));
  stock.suspended.solid *= 1 - settledFraction;
  stock.suspended.liquid *= 1 - settledFraction;
  return latentHeat;
}

export interface ReactionTerm { readonly material: MaterialId; readonly phase: Phase; readonly amount: number }
export interface Reaction { readonly reactants: readonly ReactionTerm[]; readonly products: readonly ReactionTerm[] }
export const REACTIONS = {
  photosynthesis: { reactants: [{ material: 'carbonDioxide', phase: 'gas', amount: 1 }], products: [{ material: 'carbon', phase: 'solid', amount: 1 }, { material: 'oxygen', phase: 'gas', amount: 1 }] },
  respiration: { reactants: [{ material: 'carbon', phase: 'solid', amount: 1 }, { material: 'oxygen', phase: 'gas', amount: 1 }], products: [{ material: 'carbonDioxide', phase: 'gas', amount: 1 }] },
  oxidation: { reactants: [{ material: 'iron', phase: 'solid', amount: 4 }, { material: 'oxygen', phase: 'gas', amount: 3 }], products: [{ material: 'ironOxide', phase: 'solid', amount: 2 }] },
  weathering: { reactants: [{ material: 'silicate', phase: 'solid', amount: 1 }, { material: 'carbonDioxide', phase: 'gas', amount: 1 }], products: [{ material: 'carbonate', phase: 'solid', amount: 1 }, { material: 'silica', phase: 'solid', amount: 1 }] },
  fusion: { reactants: [{ material: 'hydrogen', phase: 'plasma', amount: 4 }], products: [{ material: 'helium', phase: 'plasma', amount: 1 }] },
} satisfies Record<string, Reaction>;

const compiledReactions = new WeakMap<Reaction, { reactants: ReactionTerm[]; products: ReactionTerm[] }>();

export type MaterialChangeObserver = (stock: MaterialInventory, phase: Phase, amount: number, suspendedAmount: number) => void;

export function react(materials: MaterialInventory[], reaction: Reaction, requested: number, observer?: MaterialChangeObserver): number {
  if (!Number.isFinite(requested) || requested < 0) throw new RangeError('Reaction extent must be finite and non-negative.');
  // Reaction definitions are readonly. Compile once, not per surface cell.
  let compiled = compiledReactions.get(reaction);
  if (compiled === undefined) {
    compiled = { reactants: aggregateTerms(reaction.reactants), products: aggregateTerms(reaction.products) };
    compiledReactions.set(reaction, compiled);
  }
  const { reactants, products } = compiled;
  if (reactants.length === 0) throw new RangeError('A reaction requires at least one reactant.');
  let extent = Math.max(0, requested);
  for (const term of reactants) extent = Math.min(extent, (materials.find(stock => stock.material === term.material)?.phases[term.phase] ?? 0) / term.amount);
  if (extent <= 0) return 0;
  for (const term of reactants) {
    const stock = materials.find(stock => stock.material === term.material)!;
    const oldAmount = stock.phases[term.phase];
    const oldSuspended = term.phase === 'solid' || term.phase === 'liquid' ? stock.suspended[term.phase] : 0;
    stock.phases[term.phase] = Math.max(0, stock.phases[term.phase] - extent * term.amount);
    if (term.phase === 'solid' || term.phase === 'liquid') stock.suspended[term.phase] *= stock.phases[term.phase] / oldAmount;
    observer?.(stock, term.phase, -extent * term.amount,
      term.phase === 'solid' || term.phase === 'liquid' ? stock.suspended[term.phase] - oldSuspended : 0);
  }
  for (const term of products) {
    let stock = materials.find(stock => stock.material === term.material);
    if (stock === undefined) { stock = inventory(term.material, {}); materials.push(stock); }
    stock.phases[term.phase] += extent * term.amount;
    observer?.(stock, term.phase, extent * term.amount, 0);
  }
  return extent;
}

function aggregateTerms(terms: readonly ReactionTerm[]): ReactionTerm[] {
  const combined = new Map<string, ReactionTerm>();
  for (const term of terms) {
    if (!(term.material in MATERIALS) || !PHASES.includes(term.phase) || !Number.isFinite(term.amount) || term.amount <= 0) {
      throw new RangeError('Reaction terms require a registered material, phase, and positive finite coefficient.');
    }
    const key = `${term.material}:${term.phase}`;
    combined.set(key, { ...term, amount: term.amount + (combined.get(key)?.amount ?? 0) });
  }
  return [...combined.values()];
}
